import { reactive } from 'vue';

// App-owned dialog state driving a single shared NoticeDialog instance
// (unified notices rollout — see Documents/lumi/ux/notices). Two entry points:
//   useConfirm().confirm(opts)  → Promise<boolean>  (decision mode; replaces window.confirm)
//   useNotice().error|warning|info|success({ title, body, reference })  (acknowledge mode)
// The lib component is headless; this is the per-app glue. Single instance,
// last-wins — apps rarely stack blocking dialogs.
type Variant = 'error' | 'warning' | 'info' | 'success';
type Tone = 'primary' | 'danger';

interface DialogState {
  open: boolean;
  variant: Variant;
  mode: 'acknowledge' | 'decision';
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmTone: Tone;
  reference?: string;
  busy: boolean;
}

interface ConfirmOptions {
  title: string;
  body?: string;
  variant?: Variant;
  confirmTone?: Tone;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface NoticeOptions {
  title: string;
  body?: string;
  reference?: string;
  confirmLabel?: string;
}

const state = reactive<DialogState>({
  open: false,
  variant: 'info',
  mode: 'acknowledge',
  title: '',
  body: '',
  confirmTone: 'primary',
  busy: false,
});

let resolver: ((value: boolean) => void) | null = null;
// A notice that arrived while a decision (confirm) dialog was still open, held
// back so it cannot silently resolve the user's pending confirm as "cancel".
let deferredNotice: (() => void) | null = null;

function settle(value: boolean) {
  state.open = false;
  const r = resolver;
  resolver = null;
  r?.(value);
  if (deferredNotice) {
    const show = deferredNotice;
    deferredNotice = null;
    show();
  }
}

export function useDialog() {
  function confirm(opts: ConfirmOptions): Promise<boolean> {
    settle(false); // close any in-flight dialog before opening a new one
    Object.assign(state, {
      open: true,
      mode: 'decision' as const,
      variant: opts.variant ?? 'warning',
      confirmTone: opts.confirmTone ?? 'primary',
      title: opts.title,
      body: opts.body ?? '',
      confirmLabel: opts.confirmLabel,
      cancelLabel: opts.cancelLabel,
      reference: undefined,
      busy: false,
    });
    return new Promise<boolean>((res) => {
      resolver = res;
    });
  }

  function notice(variant: Variant, opts: NoticeOptions) {
    // Never preempt a pending confirm() — resolving it as false would look like
    // the user clicked Cancel. Defer until the decision settles, then show.
    if (resolver) {
      deferredNotice = () => notice(variant, opts);
      return;
    }
    settle(false);
    Object.assign(state, {
      open: true,
      mode: 'acknowledge' as const,
      variant,
      confirmTone: 'primary' as const,
      title: opts.title,
      body: opts.body ?? '',
      confirmLabel: opts.confirmLabel,
      cancelLabel: undefined,
      reference: opts.reference,
      busy: false,
    });
  }

  return {
    state,
    confirm,
    notice,
    error: (opts: NoticeOptions) => notice('error', opts),
    warning: (opts: NoticeOptions) => notice('warning', opts),
    info: (opts: NoticeOptions) => notice('info', opts),
    success: (opts: NoticeOptions) => notice('success', opts),
    onConfirm: () => settle(true),
    onCancel: () => settle(false),
    onClose: () => settle(false),
  };
}

// Convenience aliases matching the recipe vocabulary.
export const useConfirm = () => {
  const d = useDialog();
  return { confirm: d.confirm };
};
export const useNotice = () => {
  const d = useDialog();
  return { error: d.error, warning: d.warning, info: d.info, success: d.success };
};
